import React, { useEffect, useState } from 'react';
import { Button, ListGroup, Modal, Form, Card, Row, Col, ToggleButton, ToggleButtonGroup, Badge } from 'react-bootstrap';

const EmployeesTab = ({ user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [position, setPosition] = useState('');
  const [skills, setSkills] = useState('');
  const [companyName] = useState(user?.company_name || '');
  const [rate, setRate] = useState('');
  const [availability, setAvailability] = useState('');
  const [cv, setCv] = useState(null);
  const [cvPreview, setCvPreview] = useState(null);
  const [qualification, setQualification] = useState([{ university: '', year: '', course: '' }]);
  const [experience, setExperience] = useState([{ company: '', role: '', from: '', to: '' }]);
  const API_URL = process.env.REACT_APP_API_URL;

  const fetchEmployees = async () => {
    try {
      const url = viewMode === 'all'
        ? `${API_URL}/private/api/employees/all`
        : `${API_URL}/private/api/employees/${user._id}`;
      const res = await fetch(url);
      const data = await res.json();
      setEmployees(data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  useEffect(() => { fetchEmployees(); }, [viewMode]);

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append('b2bId', user._id);
    formData.append('companyName', companyName);
    formData.append('name', name);
    formData.append('email', email);
    formData.append('position', position);
    formData.append('skills', skills);
    formData.append('rate', rate);
    formData.append('availability', availability);
    formData.append('qualification', JSON.stringify(qualification));
    formData.append('experience', JSON.stringify(experience));
    if (cv) formData.append('cv', cv);

    try {
      const res = await fetch(`${API_URL}/private/api/employees`, { method: 'POST', body: formData });

      if (!res.ok) {
        const errorData = await res.json();
        console.error('Error:', errorData);
        alert("Failed to save employee");
        return;
      }

      setShowModal(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      console.error('Error while saving:', err);
      alert('Something went wrong!');
    }
  };

  const handleUpdate = async () => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('email', email);
    formData.append('position', position);
    formData.append('skills', skills);
    formData.append('rate', rate);
    formData.append('availability', availability);
    formData.append('qualification', JSON.stringify(qualification));
    formData.append('experience', JSON.stringify(experience));
    if (cv) formData.append('cv', cv);

    try {
      const res = await fetch(`${API_URL}/private/api/employees/${selectedEmployee._id}`, {
        method: 'PUT',
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error('Error:', errorData);
        alert("Failed to update employee");
        return;
      }

      setIsEditing(false);
      fetchEmployees();
    } catch (err) {
      console.error('Error while updating:', err);
      alert('Something went wrong!');
    }
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setPosition('');
    setSkills('');
    setRate('');
    setAvailability('');
    setQualification([{ university: '', year: '', course: '' }]);
    setExperience([{ company: '', role: '', from: '', to: '' }]);
    setCv(null);
    setCvPreview(null);
  };

  const handleEmployeeClick = (emp) => {
    setSelectedEmployee(emp);
    setName(emp.name);
    setEmail(emp.email);
    setPosition(emp.position);
    setSkills(emp.skills);
    setRate(emp.rate);
    setAvailability(emp.availability);
    setQualification(emp.qualification || []);
    setExperience(emp.experience || []);

    if (emp.cv?.data) {
      const byteArray = new Uint8Array(emp.cv.data.data);
      const blob = new Blob([byteArray], { type: emp.cv.contentType });
      setCvPreview(URL.createObjectURL(blob));
    }
  };

  const isMyEmployee = (emp) => emp.b2bId === user._id;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Employees</h4>
        <Button onClick={() => { resetForm(); setShowModal(true); }}>+ Add Employee</Button>
      </div>

      {!selectedEmployee && (
        <>
          <div className="mb-3 d-flex gap-2 align-items-center">
            <strong>View:</strong>
            <ToggleButtonGroup type="radio" name="viewMode" value={viewMode} onChange={val => setViewMode(val)}>
              <ToggleButton id="view-all" value="all" variant={viewMode === 'all' ? 'primary' : 'outline-primary'}>All Employees</ToggleButton>
              <ToggleButton id="view-mine" value="mine" variant={viewMode === 'mine' ? 'primary' : 'outline-primary'}>My Employees</ToggleButton>
            </ToggleButtonGroup>
          </div>

          <ListGroup>
            {employees.map(e => (
              <ListGroup.Item key={e._id} action onClick={() => handleEmployeeClick(e)}>
                <strong>{e.name}</strong> - {e.position} <small className="text-muted">({e.skills || 'No skill'})</small>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </>
      )}

      {selectedEmployee && (
        <div className="mt-4">

          <Button variant="link" onClick={() => setSelectedEmployee(null)}>← Back to Employees</Button>

          <Card className="p-4 shadow-sm" id='emp-details'>
            <h3 className="mb-4 text-center">{name}</h3>

            {/* Basic Info */}
            <Row className="mb-3">
              <Col md={6}><Form.Label>Email:</Form.Label>
                {isEditing ? <Form.Control value={email} onChange={e => setEmail(e.target.value)} /> : <p>{email}</p>}
              </Col>
              <Col md={6}><Form.Label>Position:</Form.Label>
                {isEditing ? <Form.Control value={position} onChange={e => setPosition(e.target.value)} /> : <p>{position}</p>}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}><Form.Label>Skills:</Form.Label>
                {isEditing ?
                  <Form.Control value={skills} onChange={e => setSkills(e.target.value)} />
                  : <div>{skills?.split(',').map((s, i) => <Badge key={i} bg="secondary" className="me-1">{s.trim()}</Badge>)}</div>}
              </Col>

              <Col md={6}><Form.Label>Rate:</Form.Label>
                {isEditing ? <Form.Control value={rate} onChange={e => setRate(e.target.value)} /> : <p>{rate}</p>}
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}><Form.Label>Availability:</Form.Label>
                {isEditing ? <Form.Control value={availability} onChange={e => setAvailability(e.target.value)} /> : <p>{availability}</p>}
              </Col>
            </Row>

            {/* Qualification */}
            <h6>Qualification</h6>
            {qualification?.map((q, idx) => (
              <Row key={idx} className="mb-2">
                <Col><Form.Label>University:</Form.Label>
                  {isEditing ?
                    <Form.Control value={q.university} onChange={e => {
                      const updated = [...qualification]; updated[idx].university = e.target.value; setQualification(updated);
                    }} /> : <p>{q.university}</p>}
                </Col>
                <Col><Form.Label>Year:</Form.Label>
                  {isEditing ?
                    <Form.Control value={q.year} onChange={e => {
                      const updated = [...qualification]; updated[idx].year = e.target.value; setQualification(updated);
                    }} /> : <p>{q.year}</p>}
                </Col>
                <Col><Form.Label>Course:</Form.Label>
                  {isEditing ?
                    <Form.Control value={q.course} onChange={e => {
                      const updated = [...qualification]; updated[idx].course = e.target.value; setQualification(updated);
                    }} /> : <p>{q.course}</p>}
                </Col>
              </Row>
            ))}

            {/* Experience */}
            <h6 className="mt-3">Experience</h6>
            {experience?.map((exp, idx) => (
              <Row key={idx} className="mb-2">
                <Col><Form.Label>Company:</Form.Label>
                  {isEditing ?
                    <Form.Control value={exp.company} onChange={e => {
                      const updated = [...experience]; updated[idx].company = e.target.value; setExperience(updated);
                    }} /> : <p>{exp.company}</p>}
                </Col>

                <Col><Form.Label>Role:</Form.Label>
                  {isEditing ?
                    <Form.Control value={exp.role} onChange={e => {
                      const updated = [...experience]; updated[idx].role = e.target.value; setExperience(updated);
                    }} /> : <p>{exp.role}</p>}
                </Col>

                <Col><Form.Label>From:</Form.Label>
                  {isEditing ?
                    <Form.Control value={exp.from} onChange={e => {
                      const updated = [...experience]; updated[idx].from = e.target.value; setExperience(updated);
                    }} /> : <p>{exp.from}</p>}
                </Col>

                <Col><Form.Label>To:</Form.Label>
                  {isEditing ?
                    <Form.Control value={exp.to} onChange={e => {
                      const updated = [...experience]; updated[idx].to = e.target.value; setExperience(updated);
                    }} /> : <p>{exp.to}</p>}
                </Col>
              </Row>
            ))}

            {/* CV Preview */}
            {/* {isMyEmployee(selectedEmployee) && cvPreview && (
              <>
                <h6 className="mt-4">CV Preview</h6>
                <embed src={cvPreview} type="application/pdf" width="100%" height="400px" />
              </>
            )} */}

            {/* CV Preview + Upload */}
            {isMyEmployee(selectedEmployee) && (
              <>
                <h6 className="mt-4">CV</h6>
                {cvPreview && (
                  <div className="mb-3">
                    <embed src={cvPreview} type="application/pdf" width="100%" height="400px" />
                  </div>
                )}

                {isEditing && (
                  <Form.Group className="mt-3">
                    <Form.Label>Upload New CV (PDF)</Form.Label>
                    <Form.Control type="file" accept=".pdf" onChange={e => {
                      const file = e.target.files[0];
                      setCv(file);
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = () => setCvPreview(reader.result);
                        reader.readAsDataURL(file);
                      }
                    }} />
                  </Form.Group>
                )}
              </>
            )}

            {/* Buttons */}
            <div className="text-center mt-4">
              {isMyEmployee(selectedEmployee) ? (
                isEditing ? (
                  <>
                    <Button variant="success" onClick={handleUpdate}>Save</Button>
                    <Button variant="secondary" className="ms-2" onClick={() => setIsEditing(false)}>Cancel</Button>
                  </>
                ) : (
                  <Button variant="primary" onClick={() => setIsEditing(true)}>Edit</Button>
                )
              ) : null}
            </div>
          </Card>
        </div>
      )}


      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>Add Employee</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control value={name} onChange={e => setName(e.target.value)} />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control value={email} onChange={e => setEmail(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Rate</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., €20/hour"
                value={rate}
                onChange={e => setRate(e.target.value)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Position</Form.Label>
              <Form.Control value={position} onChange={e => setPosition(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Skills</Form.Label>
              <Form.Control value={skills} onChange={e => setSkills(e.target.value)} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Company Name</Form.Label>
              <Form.Control value={companyName} readOnly />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Availability</Form.Label>
              <Form.Control value={availability} onChange={e => setAvailability(e.target.value)} />
            </Form.Group>

            <hr />

            <h6>Qualification</h6>
            {qualification.map((q, i) => (
              <Row key={i} className="mb-2">
                <Col>
                  <Form.Control
                    placeholder="University"
                    value={q.university}
                    onChange={(e) => {
                      const updated = [...qualification];
                      updated[i].university = e.target.value;
                      setQualification(updated);
                    }}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="Year"
                    value={q.year}
                    onChange={(e) => {
                      const updated = [...qualification];
                      updated[i].year = e.target.value;
                      setQualification(updated);
                    }}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="Course"
                    value={q.course}
                    onChange={(e) => {
                      const updated = [...qualification];
                      updated[i].course = e.target.value;
                      setQualification(updated);
                    }}
                  />
                </Col>
              </Row>
            ))}
            <Button variant="link" onClick={() => setQualification([...qualification, { university: '', year: '', course: '' }])}>+ Add More</Button>

            <hr />

            <h6>Experience</h6>
            {experience.map((exp, i) => (
              <Row key={i} className="mb-2">
                <Col>
                  <Form.Control
                    placeholder="Company"
                    value={exp.company}
                    onChange={(e) => {
                      const updated = [...experience];
                      updated[i].company = e.target.value;
                      setExperience(updated);
                    }}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="Role"
                    value={exp.role}
                    onChange={(e) => {
                      const updated = [...experience];
                      updated[i].role = e.target.value;
                      setExperience(updated);
                    }}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="From"
                    value={exp.from}
                    onChange={(e) => {
                      const updated = [...experience];
                      updated[i].from = e.target.value;
                      setExperience(updated);
                    }}
                  />
                </Col>
                <Col>
                  <Form.Control
                    placeholder="To"
                    value={exp.to}
                    onChange={(e) => {
                      const updated = [...experience];
                      updated[i].to = e.target.value;
                      setExperience(updated);
                    }}
                  />
                </Col>
              </Row>
            ))}
            <Button variant="link" onClick={() => setExperience([...experience, { company: '', role: '', from: '', to: '' }])}>+ Add More</Button>

            <hr />

            <Form.Group className="mb-3">
              <Form.Label>Upload CV (PDF)</Form.Label>
              <Form.Control type="file" accept=".pdf" onChange={e => setCv(e.target.files[0])} />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer><Button variant="primary" onClick={handleSubmit}>Save</Button></Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmployeesTab;
